import {
  Building2,
  HardHat,
  Gauge,
  Lightbulb,
  Power,
  PlugZap,
  Wrench,
  Home,
  Factory,
  Store,
  ShieldCheck,
} from "lucide-react";

// Verified services from the client brief. Descriptions are intentionally
// general and factual — no unsupported claims about brands, warranties,
// certifications, response times, or guarantees.
export const SERVICES = [
  {
    slug: "residential-electrical",
    name: "Residential Electrical",
    Icon: Home,
    category: "Residential",
    short: "Wiring, upgrades, and repairs for homes of every size.",
    description:
      "Electrical work for houses, condos, and multi-unit residences — from everyday repairs to whole-home wiring and upgrades.",
    workTypes: [
      "Outlets, switches, and dedicated circuits",
      "Rewiring and wiring for remodels and additions",
      "Ceiling fans, fixtures, and recessed lighting",
      "Electrical repairs and safety corrections",
    ],
  },
  {
    slug: "commercial-electrical",
    name: "Commercial Electrical",
    Icon: Store,
    category: "Commercial",
    short: "Reliable electrical for offices, retail, and tenant spaces.",
    description:
      "Electrical installation and service for offices, retail, restaurants, and tenant improvements. Kensmarc Electric works with property owners and businesses to keep spaces powered and ready for operation.",
    workTypes: [
      "Tenant improvements and buildouts",
      "Lighting installation and retrofits",
      "Dedicated circuits for equipment",
      "Service and troubleshooting",
    ],
  },
  {
    slug: "industrial-electrical",
    name: "Industrial Electrical",
    Icon: Factory,
    category: "Industrial",
    short: "Power, distribution, and equipment hookups for facilities.",
    description:
      "Electrical work for warehouses, production, and industrial facilities — including power distribution, conduit, and equipment connections handled by an experienced electrical contractor.",
    workTypes: [
      "Power distribution and sub-panels",
      "Conduit and raceway installation",
      "Equipment and machinery hookups",
      "Facility electrical maintenance",
    ],
  },
  {
    slug: "panel-upgrades",
    name: "Panel Upgrades",
    Icon: Gauge,
    category: "Residential",
    short: "Upgrade aging or overloaded electrical panels safely.",
    description:
      "Replacing and upgrading electrical panels and service equipment to support today's electrical loads. A good place to start when adding major appliances, EV charging, or expanding a space.",
    workTypes: [
      "Electrical panel replacement",
      "Service capacity upgrades",
      "Adding circuits and breakers",
      "Correcting outdated or unsafe panels",
    ],
  },
  {
    slug: "lighting-electrical",
    name: "Lighting & Electrical",
    Icon: Lightbulb,
    category: "Residential",
    short: "Interior, exterior, and specialty lighting done right.",
    description:
      "Lighting design and installation for homes and businesses, plus general electrical work. From recessed lighting to exterior and security lighting, Kensmarc Electric handles the wiring and the finish.",
    workTypes: [
      "Recessed and accent lighting",
      "Exterior and landscape lighting",
      "Lighting retrofits and controls",
      "General electrical installations",
    ],
  },
  {
    slug: "generator-installation",
    name: "Generator Installation",
    Icon: Power,
    category: "Residential",
    short: "Backup power installation for homes and businesses.",
    description:
      "Installation of backup generators and the supporting electrical work so you have power when the grid goes down. Kensmarc Electric handles the electrical connections, transfer equipment, and final tie-in.",
    workTypes: [
      "Standby generator electrical hookups",
      "Transfer switch installation",
      "Dedicated circuits and wiring",
      "Backup power planning",
    ],
  },
  {
    slug: "ev-charger-installation",
    name: "EV Charger Installation",
    Icon: PlugZap,
    category: "Residential",
    short: "Home and commercial EV charging installations.",
    description:
      "Installation of electric vehicle chargers for homes and businesses, including the dedicated circuit and any panel work needed to support the charger.",
    workTypes: [
      "Home EV charger installation",
      "Dedicated 240V circuits",
      "Panel evaluation for charging loads",
      "Commercial and multi-unit charging",
    ],
  },
  {
    slug: "troubleshooting-repairs",
    name: "Troubleshooting & Repairs",
    Icon: Wrench,
    category: "Residential",
    short: "Find and fix electrical problems at the source.",
    description:
      "Diagnosing and repairing electrical issues — from tripping breakers and dead outlets to flickering lights and faults. Kensmarc Electric locates the root cause and makes the correction.",
    workTypes: [
      "Diagnosing breakers and circuit faults",
      "Repairing outlets, switches, and fixtures",
      "Correcting wiring and safety issues",
      "Whole-home electrical inspections",
    ],
  },
  {
    slug: "new-construction",
    name: "New Construction",
    Icon: HardHat,
    category: "Commercial",
    short: "Complete electrical for ground-up projects.",
    description:
      "Electrical for ground-up residential and commercial construction — from rough-in through finish. Kensmarc Electric coordinates with builders and inspectors to keep projects moving.",
    workTypes: [
      "Rough-in wiring and layout",
      "Panels and service installation",
      "Finish devices, fixtures, and trim",
      "Coordination with builders and inspections",
    ],
  },
  {
    slug: "commercial-projects",
    name: "Commercial Electrical Projects",
    Icon: Building2,
    category: "Commercial",
    short: "Larger-scale commercial electrical, start to finish.",
    description:
      "Project-based commercial electrical work for owners, managers, and contractors. Kensmarc Electric takes on larger commercial scopes with planning, installation, and final walk-through.",
    workTypes: [
      "Project planning and coordination",
      "Distribution and lighting installation",
      "Equipment power and connections",
      "Punch-list and final inspection support",
    ],
  },
];

export const CCTV_SERVICE = {
  slug: "cctv-security",
  name: "CCTV & Security Camera Installation",
  Icon: ShieldCheck,
  category: "CCTV / Security",
  short: "Surveillance and security camera installation for home and business.",
  workTypes: ["Security camera installation", "CCTV / surveillance systems"],
};

// Options for the "Service Needed" dropdown on the quote form.
export const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.name),
  "CCTV / Security Camera Installation",
  "Electrical / Other",
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
